import { apiInstanceFactory } from '@sceneio/api-client/lib/apiInstanceFactory'
import {
  useAutoCreateProject,
  useForceReloadNotification,
  useGetUserSegmentation,
  useInternalGraphql,
  useRedirectToHomepage,
  useRouter,
} from '@sceneio/cms-hooks'
import { setGraphqlClientCommonOptions } from '@sceneio/graphql-queries/src'
import { useCommandSubscription } from '@sceneio/graphql-subscriptions'
import { stringify } from '@sceneio/tools'
import { useEffect } from 'react'
import { useFeaturebaseWidgetScript } from '../../externals/useFeaturebaseWidgetScript'
import { resolveBypassHeader } from '../../helpers/resolveBypassHeader'
import { USER } from '../../store/redux/constatnts'
import { dispatchType } from '../../store/storeInstance'
import { useCheckProjectInUrl } from '../project/useCheckProjectInUrl'
import useParseQueryString from '../queryString/useParseQueryString'
import { useUserStore } from '../store/storeHooks'
import { useTrackReturningUser } from '../tracking/useTrackReturningUser'
import { useCommandCallback } from '../ws/useCommandCallback'

export function removeQueryParamsFromUrl() {
  window.history.replaceState(null, '', `/${window.location.hash}`)
}

const backendUrl = import.meta.env.VITE_BACKEND_URL

export default function useLayoutInitializer() {
  // SET TOKEN TO REQUIRED LIBS
  const {
    token,
    selectedProject,
    createdAt,
    hasCompletedSegmentation,
    myWorkspaceId,
  } = useUserStore()
  const { pushRoute } = useRouter()
  const queryParams = useParseQueryString()

  useTrackReturningUser()

  useFeaturebaseWidgetScript()

  const { asyncResultResolver } = useCommandCallback()
  const redirectToHomepage = useRedirectToHomepage()
  const autoCreateProject = useAutoCreateProject()
  const getUserSegmentation = useGetUserSegmentation()
  const forceReloadNotification = useForceReloadNotification({
    onLogout() {
      dispatchType(USER.LOGOUT, null)
    },
  })

  const { sendRequest: fetchAdditionalUserData } = useInternalGraphql({
    query: 'UserAdditionalQueryDocument',
    runOnInit: false,
  })

  // CONTENT API CLIENT
  // TODO merge CMS apiClient
  apiInstanceFactory({
    url: backendUrl,
    token,
  })

  // new graphql client
  setGraphqlClientCommonOptions({
    url: backendUrl,
    token: token!,
    headers: resolveBypassHeader(),
    onQueryHashError() {
      forceReloadNotification()
    },

    // onQueryStart() {
    //   dispatchType(UI.SET_LOADING_START)
    // },
    // onQueryEnd() {
    //   dispatchType(UI.SET_LOADING_STOP)
    // },
  })

  useEffect(() => {
    const { redirectOnFirstLogin, themeId } = queryParams
    if (redirectOnFirstLogin && selectedProject) {
      // new users should land directly on untitled project canvas after sign up
      redirectToHomepage({ projectId: selectedProject?.id, themeId })
    } else if (!redirectOnFirstLogin && themeId) {
      // existing users should land on new project canvas with preselected theme
      const url = new URL(window.location.href)
      if (url.pathname === '/') {
        autoCreateProject({ workspaceId: myWorkspaceId, themeId })
      }
    }
  }, [stringify(queryParams), myWorkspaceId])

  // CHECK PROJECT IN URL
  useCheckProjectInUrl()

  // fetch missing user hasCompletedSegmentation
  useEffect(() => {
    if (typeof hasCompletedSegmentation !== 'boolean') {
      getUserSegmentation().then(({ hasCompletedSegmentation }) => {
        dispatchType(USER.UPDATE, { hasCompletedSegmentation })
      })
    }
  }, [hasCompletedSegmentation])

  // fetch missing user data
  useEffect(() => {
    if (!createdAt || !myWorkspaceId) {
      fetchAdditionalUserData().then(({ data }) => {
        dispatchType(USER.UPDATE, {
          createdAt: data?.me?.createdAt,
          myWorkspaceId: data?.me?.assignedWorkspaces?.[0]?.workspace?.id,
        })
      })
    }
  }, [createdAt, myWorkspaceId])

  // Magic Link resolver when authenticated
  useEffect(() => {
    const { project, path, content, auth, commentId, showPlans } = queryParams

    if (project || auth) {
      removeQueryParamsFromUrl()
    }

    if (project && content && path) {
      pushRoute(
        'liveEditorContent',
        {
          contentId: content,
          pathId: path,
          projectId: project,
        },
        {
          commentId,
        },
      )
    } else if (project) {
      pushRoute(
        'content',
        {
          projectId: project,
        },
        { showPlans },
      )
    }
  }, [])

  // register to global command subscription
  useCommandSubscription({
    callbacks: {
      asyncResult: asyncResultResolver,
    },
  })
}
